import { Button, useToast } from "@au-schein-de/au-ui-library";
import * as Sentry from "@sentry/react-router";
import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
	useFetcher,
	useLoaderData,
	useNavigate,
	useRevalidator,
	useRouteLoaderData,
} from "react-router";
import { useCurrency } from "~/hooks/use-currency-price.hook";
import { buildSsoLoginRedirectPath } from "~/tooling/authentication/redirect-to-sso-login";
import {
	pushCheckoutFailedMetric,
	pushProceedToCheckoutMetric,
} from "~/tooling/sentry";
import { captureCheckoutStartedOnce } from "~/tooling/telemetry/checkout-step-once";
import type { Route as RootRoute } from "../../+types/root";
import type { Route } from "../../routes/+types/_storefront";
import { initializeCheckout } from "../checkout/checkout.context";
import { CHECKOUT_STEP_NAMES } from "../checkout/constants";
import { useCart } from "./cart.context";

export function CartCheckoutButton() {
	const { t } = useTranslation("cart");
	const { toast } = useToast();
	const navigate = useNavigate();
	const checkoutFetcher = useFetcher();
	const revalidator = useRevalidator();
	const pendingCheckout = useRef(false);
	const previousRevalidatorState = useRef(revalidator.state);
	const { cartDisclosure, remainingCapacity, isLoading } = useCart();

	const rootLoaderData =
		useRouteLoaderData<RootRoute.ComponentProps["loaderData"]>("root");
	const { cart, user, customer, stockValidationFlagEnabled } =
		useLoaderData<Route.ComponentProps["loaderData"]>();
	const isLoggedIn = !!user && !!customer;
	const currency = useCurrency();
	const enableSentryCustomEvents =
		rootLoaderData?.featureFlags?.enableSentryCustomEvents ?? false;

	const cartStockValidation =
		"stockValidation" in cart ? cart.stockValidation : null;

	const hasUnavailableItems =
		stockValidationFlagEnabled &&
		cartStockValidation !== null &&
		!cartStockValidation.allAvailable;

	const proceedToCheckout = useCallback(async () => {
		if (hasUnavailableItems) return;

		if (!isLoggedIn) {
			window.location.href = buildSsoLoginRedirectPath(
				CHECKOUT_STEP_NAMES.prescriptionOrder,
			);
			return;
		}

		let redirectTo: string;

		try {
			redirectTo = await initializeCheckout({
				user,
				cart,
			});
		} catch (error) {
			Sentry.captureException(error, {
				extra: {
					cartId: cart.cartId,
					vendorId: cart.vendor?.id,
					vendorName: cart.vendor?.name,
				},
			});
			pushCheckoutFailedMetric(
				{
					cartId: cart.cartId,
					errorMessage:
						error instanceof Error ? error.message : "Unknown error",
					vendorId: cart.vendor?.id,
					vendorName: cart.vendor?.name,
				},
				enableSentryCustomEvents,
			);
			toast({
				title: t("errors.checkoutFailed"),
				variant: "destructive",
			});
			return;
		}

		pushProceedToCheckoutMetric(
			{
				cartId: cart.cartId,
				totalItems: cart.items.reduce((acc, item) => acc + item.quantity, 0),
				totalPrice: cart.totalPrice,
				vendorId: cart.vendor?.id,
				vendorName: cart.vendor?.name,
			},
			enableSentryCustomEvents,
		);

		cartDisclosure.close();

		captureCheckoutStartedOnce(cart, currency);

		navigate(redirectTo);
	}, [
		hasUnavailableItems,
		isLoggedIn,
		user,
		cart,
		cartDisclosure,
		currency,
		navigate,
		enableSentryCustomEvents,
		toast,
		t,
	]);

	const onCheckout = async () => {
		if (stockValidationFlagEnabled) {
			if (revalidator.state === "idle") {
				pendingCheckout.current = true;
				revalidator.revalidate();
			}
			return;
		}

		await proceedToCheckout();
	};

	useEffect(() => {
		const justFinishedRevalidating =
			previousRevalidatorState.current === "loading" &&
			revalidator.state === "idle";

		previousRevalidatorState.current = revalidator.state;

		if (
			pendingCheckout.current &&
			justFinishedRevalidating &&
			stockValidationFlagEnabled &&
			!isLoading
		) {
			pendingCheckout.current = false;
			const allItemsAvailable = cartStockValidation?.allAvailable === true;
			if (allItemsAvailable) {
				proceedToCheckout();
			}
		}
	}, [
		revalidator.state,
		cartStockValidation,
		stockValidationFlagEnabled,
		isLoading,
		proceedToCheckout,
	]);

	if (remainingCapacity < 0) {
		return (
			<Button
				data-testid="cart-full-checkout-button"
				className="px-2"
				variant="secondary"
				disabled
			>
				<span className="whitespace-normal">{t("limit.reduceAmount")}</span>
			</Button>
		);
	}

	return (
		<Button
			onClick={onCheckout}
			data-testid="cart-checkout-button"
			className="px-2"
			pending={
				checkoutFetcher.state !== "idle" ||
				isLoading ||
				(pendingCheckout.current && revalidator.state === "loading")
			}
			disabled={isLoading || hasUnavailableItems}
		>
			<span className="whitespace-normal">
				{t(isLoggedIn ? "checkout" : "login")}
			</span>
		</Button>
	);
}
